import { FileText, Image as ImageIcon, Video } from 'lucide-react';
import { EvidenceKind } from '@kainos/shared';
import { cn } from '../lib/cn.js';
import { EVIDENCE_KIND_LABEL } from '../labels/index.js';

const KIND_ICON = {
  [EvidenceKind.PHOTO]: ImageIcon,
  [EvidenceKind.DOCUMENT]: FileText,
  [EvidenceKind.VIDEO]: Video,
  [EvidenceKind.OTHER]: FileText,
} as const;

export interface EvidencePreviewProps {
  fileName: string;
  kind: EvidenceKind;
  /** Line under the file name, e.g. "Pruebas de continuidad · 25 ago". */
  caption?: string;
  /** Signed URL. Absent while the upload is still pending. */
  thumbnailUrl?: string;
  onClick?: () => void;
  className?: string;
}

/** Evidence tile: preview area on top, file name and caption below. */
export function EvidencePreview({
  fileName,
  kind,
  caption,
  thumbnailUrl,
  onClick,
  className,
}: EvidencePreviewProps) {
  const Icon = KIND_ICON[kind];
  const Wrapper = onClick ? 'button' : 'div';

  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'overflow-hidden rounded-xl border border-line bg-surface text-left',
        onClick && 'cursor-pointer transition-colors hover:border-primary-border',
        className,
      )}
    >
      <div className="flex h-[120px] items-center justify-center bg-line-subtle">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={fileName} className="h-full w-full object-cover" />
        ) : (
          <Icon size={30} strokeWidth={1.6} className="text-ink-faint" aria-hidden="true" />
        )}
      </div>
      <div className="px-3 py-2.5">
        <div className="truncate text-[12.5px] font-bold text-ink">{fileName}</div>
        <div className="mt-0.5 truncate text-[11px] font-medium text-ink-faint">
          {caption ?? EVIDENCE_KIND_LABEL[kind]}
        </div>
      </div>
    </Wrapper>
  );
}
